import React, { Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller } from 'react-hook-form';

// Components Styled
import { ArrowRadioGroup } from '../../../pages/main/comunications/NewComunication/styles';
import {
  ContainerBasicInfo,
  InputsContainer,
  Title,
} from '../components/MainStyled';

//Components
import { DateTime } from '../../inputs/DateTime';

// Types
import { CommunicationSendStatus } from '../../../types/communication';
import { FormProps } from './types';
import SearchableDropdown from '../../inputs/SearchableDropdown';
import { ImperativeRefSearchableDropdown } from '../../inputs/SearchableDropdown/types';
import InputText from '../../inputs/InputText';

export type BasicInfoRefs = {
  groupInterRef?: Ref<ImperativeRefSearchableDropdown>;
  mediaRef?: Ref<ImperativeRefSearchableDropdown>;
};

type BasicInfoProps = Omit<FormProps, 'setValue' | 'initialData'> & {
  refs?: BasicInfoRefs;
  /**
   * Solo el formulario de DUPLICAR: el «Operario» deja de ser texto fijo y se
   * elige entre los usuarios app (`userId`). `onOperatorChange` recibe el
   * nombre elegido, para que el resumen lo pinte.
   */
  showOperatorPicker?: boolean;
  onOperatorChange?: (name: string) => void;
};

const BasicInfo = ({
  register,
  control,
  errors,
  refs,
  showOperatorPicker,
  onOperatorChange,
}: BasicInfoProps) => {
  const { t } = useTranslation('forms');
  return (
    <ContainerBasicInfo>
      <Title component="h2" variant="semiBold" size="L">
        {t('communication.basicInfo')}
      </Title>

      <InputsContainer>
        {showOperatorPicker ? (
          // Duplicar: el autor del duplicado se elige. Arranca en el de la
          // original y el logueado va el primero de la lista (spec
          // 2026-09-24-duplicar-operario-editable § 3).
          <Controller
            control={control}
            name="userId"
            render={({ field: { onChange }, formState: { defaultValues } }) => (
              <SearchableDropdown
                id="operator-select"
                inputSize="M"
                typeDropDown="Default"
                labelText={t('communication.operator')}
                placeholder={t('common:labels.select')}
                options={
                  defaultValues?.appUserOptions?.map((user) => ({
                    label: user?.name ?? '',
                    value: user?.id ?? '',
                  })) ?? []
                }
                defaultValue={defaultValues?.userId}
                onChangeSelected={(option) => {
                  onChange(option?.value ?? '');
                  onOperatorChange?.(option?.label ?? '');
                }}
                borderError={!!errors.userId}
              />
            )}
          />
        ) : (
          <InputText
            {...register('operator')}
            disabled
            label={t('communication.operator')}
            typeInput="normal"
            borderError={!!errors.operator}
          />
        )}

        <Controller
          control={control}
          name="registerInitDate"
          render={({ field: { onChange }, formState: { defaultValues } }) => {
            return (
              <DateTime
                label={t('communication.registerDate')}
                labelHour={t('communication.hour')}
                onChangeDate={onChange}
                initialDate={
                  defaultValues?.registerInitDate
                    ? defaultValues?.registerInitDate
                    : new Date()
                }
                borderError={!!errors.registerInitDate}
              />
            );
          }}
        />

        <Controller
          control={control}
          name="groupInter"
          render={({ field: { onChange }, formState: { defaultValues } }) => {
            const options =
              defaultValues?.interlocutorGroups?.map((interlocutor) => ({
                label: interlocutor?.name || '',
                value: interlocutor?.id || '',
              })) || [];

            return (
              <SearchableDropdown
                ref={refs?.groupInterRef as React.Ref<NonNullable<ImperativeRefSearchableDropdown>>}
                inputSize="M"
                onChangeSelected={(option) => onChange(option?.value ?? '')}
                typeDropDown="Default"
                labelText={t('communication.interlocutorGroup')}
                placeholder={t('common:labels.select')}
                defaultValue={defaultValues?.groupInter}
                borderError={!!errors.groupInter}
                options={options}
              />
            );
          }}
        />

        <Controller
          control={control}
          name="media"
          render={({ field: { onChange }, formState: { defaultValues } }) => {
            const options =
              defaultValues?.mediaOfCommunications?.map((media) => ({
                label: media?.name || '',
                value: media?.id || '',
              })) || [];

            return (
              <SearchableDropdown
                ref={refs?.mediaRef as React.Ref<NonNullable<ImperativeRefSearchableDropdown>>}
                inputSize="M"
                onChangeSelected={(option) => onChange(option?.value ?? '')}
                typeDropDown="Default"
                labelText={t('communication.media')}
                placeholder={t('common:labels.select')}
                defaultValue={defaultValues?.media}
                borderError={!!errors.media}
                options={options}
              />
            );
          }}
        />

        <Controller
          control={control}
          name="sendStatus"
          render={({ field: { onChange }, formState: { defaultValues } }) => {
            const defaultSendStatusValue = {
              [CommunicationSendStatus.SENT]: 'up',
              [CommunicationSendStatus.RECEIVED]: 'down',
            };

            return (
              <ArrowRadioGroup
                directions={['up', 'down']}
                name="enviada-rebuda"
                label={[t('communication.sent'), t('communication.received')]}
                separated
                defaultValue={
                  defaultSendStatusValue[
                    defaultValues?.sendStatus as CommunicationSendStatus
                  ]
                }
                required
                borderError={!!errors.sendStatus}
                onChangeDirection={(dir) => {
                  onChange(
                    dir === 'up'
                      ? CommunicationSendStatus.SENT
                      : CommunicationSendStatus.RECEIVED
                  );
                }}
              />
            );
          }}
        />
      </InputsContainer>
    </ContainerBasicInfo>
  );
};

export default BasicInfo;
